/**
 * TcAgreementCard — Beta T&C status card for /profile?tab=settings
 * =================================================================
 * Shows:
 *   - Accepted version + timestamp (from GET /api/auth/tc-status)
 *   - "View my T&C Agreement" link → opens a modal rendering the exact
 *     frozen agreement text the user accepted (GET /api/auth/tc-my-agreement)
 *   - If `force_update` is on AND user's version differs from current → banner
 *     "A new Beta T&C version is available — please review" + accept button
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authAxios } from "../utils/auth";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { FileText, ShieldCheck, AlertTriangle, ExternalLink, Loader2 } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const inlineFormat = (t) => {
  let out = t;
  out = out.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>');
  out = out.replace(/\*(.+?)\*/g, '<em>$1</em>');
  return out;
};

const renderMarkdown = (text) => {
  const lines = text.split("\n");
  const el = [];
  let inTable = false; let rows = [];
  const flush = () => {
    if (rows.length) el.push(
      <table key={`t-${el.length}`} className="w-full border-collapse my-3 text-xs">
        <tbody>{rows.map((r, i) => (
          <tr key={i} className="border-b border-white/10">
            {r.map((c, ci) => (
              <td key={ci} className={`py-1.5 px-2 ${ci === 0 ? "font-semibold text-white/80 w-1/3" : "text-white/60"}`}>
                <span dangerouslySetInnerHTML={{ __html: inlineFormat(c) }} />
              </td>
            ))}
          </tr>
        ))}</tbody>
      </table>
    );
    rows = []; inTable = false;
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith("|") && line.endsWith("|")) {
      const cells = line.split("|").filter(Boolean).map(c => c.trim());
      if (cells.every(c => /^[-:]+$/.test(c))) continue;
      inTable = true; rows.push(cells); continue;
    }
    if (inTable) flush();
    if (line.startsWith("## ")) el.push(<h2 key={i} className="text-base font-bold text-white mt-5 mb-2">{line.slice(3)}</h2>);
    else if (line.startsWith("# ")) el.push(<h1 key={i} className="text-lg font-bold text-white mt-5 mb-2">{line.slice(2)}</h1>);
    else if (line.startsWith("---")) el.push(<hr key={i} className="border-white/10 my-4" />);
    else if (line.trim() === "") el.push(<div key={i} className="h-2" />);
    else el.push(
      <p key={i} className="text-white/70 text-xs leading-relaxed mb-1">
        <span dangerouslySetInnerHTML={{ __html: inlineFormat(line) }} />
      </p>
    );
  }
  if (inTable) flush();
  return el;
};

export const TcAgreementCard = ({ isCyber }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalDoc, setModalDoc] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await authAxios.get("/auth/tc-status");
        setStatus(data);
      } catch (_) {
        // Non-fatal — hide the card if we can't fetch status
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openMyAgreement = async () => {
    setShowModal(true);
    if (modalDoc) return;
    setModalLoading(true);
    try {
      const { data } = await authAxios.get("/auth/tc-my-agreement");
      setModalDoc(data);
    } catch (err) {
      setModalDoc({ error: err.response?.data?.detail || "No accepted agreement found." });
    } finally {
      setModalLoading(false);
    }
  };

  if (loading || !status) return null;

  const cardBase = `rounded-xl p-6 ${isCyber ? "glass-cyber" : "glass-gold"}`;
  const outdated = status.accepted && status.force_update && status.agreement_version !== status.current_version;

  return (
    <div className={cardBase} data-testid="tc-agreement-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className={`font-cinzel text-lg ${isCyber ? "text-cyan-400" : "text-white"} flex items-center gap-2`}>
          <ShieldCheck className="w-4 h-4" />
          Terms & Conditions
        </h3>
      </div>

      {status.accepted ? (
        <>
          <div className="grid grid-cols-2 gap-3 text-sm mb-4">
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-white/40 text-xs uppercase tracking-wider mb-1">Version</div>
              <div className="text-white font-mono" data-testid="tc-version">v{status.agreement_version}</div>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-white/40 text-xs uppercase tracking-wider mb-1">Accepted On</div>
              <div className="text-white text-xs" data-testid="tc-accepted-at">{formatDate(status.accepted_at)}</div>
            </div>
          </div>

          {outdated && (
            <div data-testid="tc-outdated-banner" className="flex items-start gap-2 p-3 mb-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-sm">
              <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-yellow-300">A new Beta T&C version (v{status.current_version}) is available.</p>
                <Button size="sm" onClick={() => navigate("/beta-terms")} className="mt-2 bg-yellow-500 text-black hover:bg-yellow-500/85">Review Now</Button>
              </div>
            </div>
          )}

          <button
            data-testid="view-my-tc-btn"
            onClick={openMyAgreement}
            className={`inline-flex items-center gap-1.5 text-sm underline underline-offset-2 ${isCyber ? "text-cyan-400 hover:text-cyan-300" : "text-divine-gold hover:text-divine-gold/80"}`}
          >
            <FileText className="w-3.5 h-3.5" /> View my T&C Agreement <ExternalLink className="w-3 h-3" />
          </button>
        </>
      ) : (
        <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <p className="text-yellow-300 text-sm mb-3">You have not yet accepted the Beta Terms & Conditions.</p>
          <Button data-testid="accept-tc-cta" onClick={() => navigate("/beta-terms")} className="bg-yellow-500 text-black hover:bg-yellow-500/85">Review & Accept</Button>
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent data-testid="tc-agreement-modal" className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2 text-divine-gold">
              <FileText className="w-4 h-4" />
              My Accepted Beta T&C Agreement
              {modalDoc?.agreement_version && <span className="text-xs text-gray-500 ml-2">v{modalDoc.agreement_version} · rev {modalDoc.revision}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto border border-white/10 bg-black/30 rounded-lg p-4 mt-2 text-xs">
            {modalLoading ? (
              <div className="flex items-center justify-center py-12 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading agreement…
              </div>
            ) : modalDoc?.error ? (
              <p className="text-red-400">{modalDoc.error}</p>
            ) : modalDoc?.agreement_text ? (
              renderMarkdown(modalDoc.agreement_text)
            ) : null}
          </div>
          {modalDoc?.accepted_at && (
            <p className="text-xs text-gray-500 mt-2 shrink-0">
              Accepted at {formatDate(modalDoc.accepted_at)} · IP {modalDoc.ip_address || "—"}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TcAgreementCard;
